import { useEffect, useRef, useState } from 'react'

export interface TurnstileProps {
  /**
   * The site key, from `VITE_TURNSTILE_SITE_KEY`.
   *
   * **Empty renders nothing**, and that is load-bearing rather than a fallback: there is
   * no Turnstile account yet, so an unset key must leave the form working rather than
   * showing a widget that cannot load. Same pattern as `app_partners`'s store badges,
   * which render only when their URL is set.
   */
  siteKey?: string
  /** Called with the token once the check passes, and with `null` when it expires. */
  onToken: (token: string | null) => void
  /** Visible label above the widget. */
  label: string
  /** Shown if the widget itself fails to load or errors. */
  errorLabel: string
  /** `auto` follows the page; the Partners and Steward are both light. */
  theme?: 'auto' | 'light' | 'dark'
}

/** What Cloudflare's script puts on `window`. Only the two calls used here. */
declare global {
  interface Window {
    turnstile?: {
      render: (element: HTMLElement, options: Record<string, unknown>) => string | undefined
      remove: (widgetId: string) => void
    }
  }
}

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

/**
 * Cloudflare Turnstile — the "are you a person" check.
 *
 * Chosen over reCAPTCHA for one reason that matters to this platform and one that matters
 * to this repo. **It does not profile the visitor**: no advertising identity, no
 * cross-site tracking, and it is usually invisible. And every site here is already behind
 * Cloudflare, so it is the same account and no new third party learning who reads a
 * Christian business directory in Mongolia.
 *
 * ## It renders nothing without a site key
 *
 * There is no Turnstile account yet. An unset `siteKey` leaves the form working exactly as
 * it does today rather than showing a widget stuck loading — and **the server treats a
 * missing token as a failure only when it has a secret configured**, or an unset key would
 * block every submission. Both halves are in `app_api`'s verifier.
 *
 * ## The script is loaded once, explicitly
 *
 * `render=explicit` rather than letting the script scan the DOM: an auto-rendered
 * `<div class="cf-turnstile">` races React's reconciliation and the widget ends up in a
 * node React has already replaced. Loading once and calling `render` is deterministic.
 *
 * ## The token is single-use and expires
 *
 * Cloudflare's token is good for one verification and about five minutes.
 * `expired-callback` clears it, so a form left open over lunch asks again rather than
 * posting a token the server will reject with a message nobody can act on.
 */
export function Turnstile({ siteKey, onToken, label, errorLabel, theme = 'auto' }: TurnstileProps) {
  const container = useRef<HTMLDivElement>(null)
  const widgetId = useRef<string | undefined>(undefined)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!siteKey || !container.current) return

    let cancelled = false

    const render = () => {
      if (cancelled || !container.current || !window.turnstile) return
      widgetId.current = window.turnstile.render(container.current, {
        sitekey: siteKey,
        theme,
        callback: (token: string) => onToken(token),
        'expired-callback': () => onToken(null),
        'error-callback': () => {
          setFailed(true)
          onToken(null)
        },
      })
    }

    if (window.turnstile) {
      render()
    } else {
      // One script for the page, however many widgets. A second <script> with the same src
      // re-executes and re-registers the global, which can drop a rendered widget.
      let script = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_SRC}"]`)
      if (!script) {
        script = document.createElement('script')
        script.src = SCRIPT_SRC
        script.async = true
        script.defer = true
        document.head.appendChild(script)
      }
      script.addEventListener('load', render)
      script.addEventListener('error', () => setFailed(true))
    }

    return () => {
      cancelled = true
      if (widgetId.current && window.turnstile) {
        window.turnstile.remove(widgetId.current)
        widgetId.current = undefined
      }
    }
    // `onToken` is expected to be stable (a `useCallback`). Re-rendering the widget on
    // every parent render would reset the visitor's completed check.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteKey, theme])

  // No key configured: the form works as it did before this component existed.
  if (!siteKey) return null

  return (
    <div className="ul-turnstile">
      <span className="ul-turnstile-label">{label}</span>
      <div ref={container} />
      {failed && (
        <p className="ul-turnstile-error" role="alert">
          {errorLabel}
        </p>
      )}
    </div>
  )
}
